import { getReadableShiftCodeTextColor } from "@/utils/fontSizeUtils";
import { useEffect, useMemo } from "react";

interface NurseShiftInfoSectionProps {
  isWeekendOff: boolean;
  onWeekendOffChange: (value: boolean) => void;
  fixedShift: string | null;
  onFixedShiftChange: (value: string | null) => void;
  availableShifts?: any[];
  disabled?: boolean;
}

export default function NurseShiftInfoSection({
  isWeekendOff,
  onWeekendOffChange,
  fixedShift,
  onFixedShiftChange,
  availableShifts = [],
  disabled,
}: NurseShiftInfoSectionProps) {
  useEffect(() => {
    // '주말 휴무'가 '미적용'으로 변경되면 '고정근무' 값 초기화
    if (!isWeekendOff) {
      onFixedShiftChange(null);
    }
  }, [isWeekendOff, onFixedShiftChange]);

  const fixedWorkTypeOptions = useMemo(() => {
    if (!availableShifts) return [];

    return availableShifts
      .filter((shift) => shift.shift_gb === "고정")
      .map((shift) => ({
        label: shift.name,
        value: shift.shift_id,
        color: shift.color,
      }));
  }, [availableShifts]);

  return (
    <>
      <div className="flex flex-col gap-4">
        <div>
          <label className="block text-[15px] font-semibold text-[#939393] mb-[7px] font-pretendard">
            고정근무{" "}
            <span className="text-xs text-text-light-gray font-pretendard font-light">
              *고정근무 선택시 주말휴무 작동적용
            </span>
          </label>
          <div className="grid h-auto w-full grid-cols-4 gap-1">
            {fixedWorkTypeOptions.map((option) => {
              const isSelected = fixedShift === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    if (!disabled) {
                      // 같은 버튼을 다시 클릭하면 null로 초기화
                      if (isSelected) {
                        onFixedShiftChange(null);
                      } else {
                        // 고정근무 선택 시 주말 휴무를 자동으로 적용
                        onWeekendOffChange(true);
                        onFixedShiftChange(option.value);
                      }
                    }
                  }}
                  disabled={disabled ?? false}
                  style={{
                    backgroundColor:
                      isSelected && option.color ? option.color : undefined,
                    color: isSelected && option.color ? getReadableShiftCodeTextColor(option.color) : undefined,
                    borderColor:
                      isSelected && option.color ? option.color : undefined,
                  }}
                  className={`flex-1 h-[35px] rounded-[5px] border text-sm! font-pretendard transition-colors ${
                    isSelected
                      ? option.color
                        ? "text-white"
                        : "bg-[#FE0E82] text-white border-[#FE0E82]"
                      : "bg-white text-[#939399] border-[#D6D6DE] hover:bg-gray-50 hover:border-gray-300"
                  } ${
                    disabled
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* 주말 휴무 */}
        <div>
          <div className="flex items-center justify-between">
            <label className="text-[15px] font-semibold text-[#939393] font-pretendard">
              주말 휴무
            </label>
            <div className="flex items-center gap-2">
              <span
                className={`text-[12px] font-pretendard transition-colors ${
                  isWeekendOff ? "text-[#0E59C6]" : "text-[#D6D6DE]"
                }`}
              >
                {isWeekendOff ? "적용" : "미적용"}
              </span>
              <label
                className={`relative inline-flex items-center ${
                  disabled ? "cursor-not-allowed" : "cursor-pointer"
                }`}
              >
                <input
                  type="checkbox"
                  checked={isWeekendOff}
                  onChange={(e) => onWeekendOffChange(e.target.checked)}
                  disabled={disabled ?? false}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0E59C6]"></div>
              </label>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
